import { 
  Mic, Video, FileText, Brain, Target, TrendingUp, 
  Users, Zap, Shield, Clock, Award, MessageSquare,
  Phone, Upload, Building2, Trophy
} from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

const features = [
  {
    icon: Mic,
    title: "Real-Time Voice Conversations",
    description: "Sub-second latency voice chat powered by OpenAI Realtime API. Practice like you're in the actual competition.",
    color: "primary",
  },
  {
    icon: Video,
    title: "Video Avatars",
    description: "Face-to-face practice with lifelike AI personas. Build confidence for in-person meetings and presentations.",
    color: "accent",
  },
  {
    icon: Upload,
    title: "Custom Case Builder",
    description: "Upload your own PDFs, Word docs, or build custom scenarios. Practice with any sales situation you'll encounter.",
    color: "purple",
  },
  {
    icon: Brain,
    title: "SPIN Framework Tracking",
    description: "Real-time analysis shows your Situation, Problem, Implication, and Need-Payoff questions as you practice.",
    color: "emerald",
  },
  {
    icon: Target,
    title: "Multi-Category Scoring",
    description: "Get scored on multiple professional rubric categories: Open, Needs, Value, Objections, Close, Communication, and Professionalism.",
    color: "amber",
  },
  {
    icon: TrendingUp,
    title: "Progress Analytics",
    description: "Detailed trend charts showing improvement over time. Track your scores across sessions and identify weak areas.",
    color: "primary",
  },
  {
    icon: Users,
    title: "Multiple Buyer Personas",
    description: "Practice with different personality types and difficulty levels. Each buyer responds uniquely to your approach.",
    color: "accent",
  },
  {
    icon: Zap,
    title: "Objection Drills",
    description: "Focused practice on handling objections with the Clarify-Address-Confirm and LAER frameworks.",
    color: "purple",
  },
  {
    icon: Shield,
    title: "Stay in the Ring Mode",
    description: "Challenge mode where one weak response ends the session. Build mental toughness under real pressure.",
    color: "rose",
  },
  {
    icon: Phone,
    title: "Call Recording Grading",
    description: "Import calls from Gong, Fireflies, HubSpot, and more. Get AI feedback on your real sales conversations.",
    color: "emerald",
  },
  {
    icon: Trophy,
    title: "Live Competitions",
    description: "Join real-time sales competitions with leaderboards, rooms, and judge-aligned scoring against other sellers.",
    color: "amber",
  },
  {
    icon: Building2,
    title: "Team Management",
    description: "Assign practice scenarios to your team, track completion, and monitor improvement with admin dashboards.",
    color: "primary",
  },
];

const FeatureCard = ({ feature, index }: { feature: typeof features[0]; index: number }) => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });
  
  const colorClasses = {
    primary: {
      icon: "text-primary",
      iconBg: "bg-primary/10 group-hover:bg-primary/20",
      glow: "group-hover:shadow-[0_0_30px_-5px_hsl(199_89%_48%/0.25)]",
      border: "group-hover:border-primary/30",
    },
    accent: {
      icon: "text-accent",
      iconBg: "bg-accent/10 group-hover:bg-accent/20",
      glow: "group-hover:shadow-[0_0_30px_-5px_hsl(185_85%_50%/0.25)]",
      border: "group-hover:border-accent/30",
    },
    purple: {
      icon: "text-electric-purple",
      iconBg: "bg-electric-purple/10 group-hover:bg-electric-purple/20",
      glow: "group-hover:shadow-[0_0_30px_-5px_hsl(260_70%_60%/0.25)]",
      border: "group-hover:border-electric-purple/30",
    },
    emerald: {
      icon: "text-emerald-400",
      iconBg: "bg-emerald-400/10 group-hover:bg-emerald-400/20",
      glow: "group-hover:shadow-[0_0_30px_-5px_hsl(160_84%_39%/0.25)]",
      border: "group-hover:border-emerald-400/30",
    },
    amber: {
      icon: "text-amber-400",
      iconBg: "bg-amber-400/10 group-hover:bg-amber-400/20",
      glow: "group-hover:shadow-[0_0_30px_-5px_hsl(45_93%_47%/0.25)]",
      border: "group-hover:border-amber-400/30",
    },
    rose: {
      icon: "text-rose-400",
      iconBg: "bg-rose-400/10 group-hover:bg-rose-400/20",
      glow: "group-hover:shadow-[0_0_30px_-5px_hsl(350_89%_60%/0.25)]",
      border: "group-hover:border-rose-400/30",
    },
  };

  const colors = colorClasses[feature.color as keyof typeof colorClasses];
  const Icon = feature.icon;

  return (
    <div 
      ref={ref}
      className={`group glass rounded-xl p-5 sm:p-6 border border-white/5 transition-all duration-500 ${colors.glow} ${colors.border} ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${(index % 4) * 0.1}s` }}
    >
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-all duration-300 group-hover:scale-110 ${colors.iconBg} ${colors.icon}`}>
        <Icon className="w-6 h-6" />
      </div>
      <h3 className="font-display font-semibold text-lg mb-2 text-foreground">
        {feature.title}
      </h3>
      <p className="text-muted-foreground text-sm leading-relaxed">
        {feature.description}
      </p>
    </div>
  );
};

const FeaturesShowcase = () => {
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation({ threshold: 0.2 });

  return (
    <section id="features" className="py-20 sm:py-32 relative">
      {/* Background accent */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-muted/5 to-transparent" />
      
      <div className="container mx-auto px-4 sm:px-6 relative">
        {/* Section Header */}
        <div 
          ref={headerRef}
          className={`text-center max-w-3xl mx-auto mb-16 sm:mb-20 transition-all duration-700 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <span className="inline-block px-4 py-1.5 rounded-full glass text-sm font-medium text-accent mb-4 border border-accent/20">
            Powerful Features
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
            Everything You Need to{' '}
            <span className="text-gradient bg-gradient-to-r from-primary via-accent to-electric-purple bg-clip-text text-transparent">
              Win
            </span>
          </h2>
          <p className="text-muted-foreground text-lg">
            Built specifically for sales competition preparation with features you won't find anywhere else
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
          {features.map((feature, index) => (
            <FeatureCard key={feature.title} feature={feature} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesShowcase;
